import { FC, ReactNode } from 'react'

import styled from 'styled-components'
import { Radio, RadioChangeEvent } from 'antd'

import { PaymentMethodEnum, WalletPaymentMethod } from 'src/Constants/enums'
import { MAX_MOBILE_WIDTH, MAX_TABLET_WIDTH } from 'src/Styled/variables'

import { PrimarySkeleton } from '../Skeletons/PrimarySkeleton'
import { Collapse } from '../Collapse'

import { StripeRadioButton } from './StripeRadioButton'
import { PaypalRadioButton } from './PaypalRadioButton'
import { CreditsRadioButton } from './CreditsRadioButton'
import { WalletRadioButton } from './WalletRadioButton'

const Container = styled.div`
  display: flex;
  padding: 24px;
  flex-direction: column;
  align-items: flex-start;
  gap: 24px;
  flex: 1 0 0;
  border-radius: 16px;
  border: 1px solid ${({ theme }) => theme.color.lightGrey};
  background: ${({ theme }) => theme.color.white};
  box-shadow: ${({ theme }) => theme.shadow.card};

  @media screen and (max-width: ${MAX_TABLET_WIDTH}) {
    max-width: 324px;
  }
  @media screen and (max-width: ${MAX_MOBILE_WIDTH}) {
    max-width: 100%;
  }
`

const Title = styled.div`
  color: ${({ theme }) => theme.color.darkGrey};
  font-family: 'Segoe UI';
  font-size: 18px;
  font-style: normal;
  font-weight: 600;
  line-height: 150%;
`
const MethodsContainer = styled.div`
  display: flex;
  min-width: 240px;
  flex-direction: column;
  align-items: flex-start;
  gap: 16px;
  align-self: stretch;
  min-height: 32px;
`

const MoreMethodsContainer = styled.div`
  margin-top: 40px;
`

const MethodSkeleton = styled(PrimarySkeleton)`
  height: 32px;
  border-radius: 8px;
`

const AdditionalInfoSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  align-self: stretch;
`

const Divider = styled.div`
  height: 1px;
  align-self: stretch;
  background: ${({ theme }) => theme.color.lightGrey};
`

const StyledRadioGroup = styled(Radio.Group)`
  width: 100%;
`

const StyledCollapse = styled(Collapse)`
  &.ant-collapse > .ant-collapse-item > .ant-collapse-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 16px;
  }
  .ant-collapse-header-text {
    color: ${({ theme }) => theme.color.darkGrey};
    font-family: 'Segoe UI';
    font-size: 18px;
    font-style: normal;
    font-weight: 600;
    line-height: 150%;
  }
`
export interface SchedulePaymentMethodProps {
  paymentMethod: PaymentMethodEnum
  paypalEmail?: string
  isEnoughBalance: boolean
  hasSavePaypal: boolean
  onChangeHasSavePaypal?: (value: boolean) => void
  onChange?: (paymentMethod: PaymentMethodEnum) => void
  isPaypalHidden?: boolean
  isSavePaypalHidden?: boolean
  countryCode?: string
  allowedPaymentMethods?: PaymentMethodEnum[]
  walletPaymentMethods?: WalletPaymentMethod[]
  additionalInfo?: ReactNode
  isLoading?: boolean
}

export const SchedulePaymentMethod: FC<SchedulePaymentMethodProps> = (
  props,
) => {
  const {
    paymentMethod,
    paypalEmail,
    isEnoughBalance,
    hasSavePaypal,
    onChange,
    onChangeHasSavePaypal,
    isPaypalHidden,
    isSavePaypalHidden,
    countryCode,
    allowedPaymentMethods,
    walletPaymentMethods,
    additionalInfo,
    isLoading,
  } = props

  const onChangePaymentOption = (e: RadioChangeEvent): void => {
    onChange && onChange(e?.target?.value as PaymentMethodEnum)
  }

  const isMethodAllowed = (method: PaymentMethodEnum): boolean =>
    !allowedPaymentMethods || allowedPaymentMethods.includes(method)

  const paypalValue = paypalEmail
    ? PaymentMethodEnum.PAYPAL_BILLING_AGREEMENT
    : PaymentMethodEnum.PAYPAL

  const isSavePaypalCheckboxHidden = isSavePaypalHidden || Boolean(paypalEmail)

  const showMoreMethods = paypalEmail && !isEnoughBalance
  const showCredits =
    isEnoughBalance && isMethodAllowed(PaymentMethodEnum.Credits)
  const showStripe =
    (!paypalEmail || isEnoughBalance) &&
    isMethodAllowed(PaymentMethodEnum.STRIPE)
  const showPaypal = !isPaypalHidden && isMethodAllowed(paypalValue)

  const walletRadioButtons = walletPaymentMethods?.map((method) => (
    <WalletRadioButton key={method} value={method} />
  ))

  const methods = (
    <StyledRadioGroup value={paymentMethod} onChange={onChangePaymentOption}>
      <MethodsContainer>
        {!showMoreMethods && walletRadioButtons}
        {showCredits && <CreditsRadioButton />}
        {showStripe && (
          <StripeRadioButton
            countryCode={countryCode}
            walletPaymentMethods={walletPaymentMethods}
            disabled={isEnoughBalance}
          />
        )}
        {showPaypal && (
          <PaypalRadioButton
            value={paypalValue}
            disabled={isEnoughBalance}
            paypalEmail={paypalEmail}
            paymentMethod={paymentMethod}
            hasSavePaypal={hasSavePaypal}
            onChangeHasSavePaypal={onChangeHasSavePaypal}
            isSavePaypalHidden={isSavePaypalCheckboxHidden}
          />
        )}
      </MethodsContainer>
      {showMoreMethods && (
        <MoreMethodsContainer>
          <StyledCollapse
            items={[
              {
                label: 'More methods',
                children: (
                  <MethodsContainer>
                    {walletRadioButtons}
                    {isMethodAllowed(PaymentMethodEnum.STRIPE) && (
                      <StripeRadioButton
                        countryCode={countryCode}
                        walletPaymentMethods={walletPaymentMethods}
                      />
                    )}
                    {isMethodAllowed(PaymentMethodEnum.PAYPAL) && (
                      <PaypalRadioButton
                        value={PaymentMethodEnum.PAYPAL}
                        paymentMethod={paymentMethod}
                        hasSavePaypal={hasSavePaypal}
                        onChangeHasSavePaypal={onChangeHasSavePaypal}
                        isSavePaypalHidden={isSavePaypalCheckboxHidden}
                      />
                    )}
                  </MethodsContainer>
                ),
              },
            ]}
          />
        </MoreMethodsContainer>
      )}
    </StyledRadioGroup>
  )

  return (
    <Container>
      <Title>Select payment method</Title>
      {isLoading ? (
        <MethodsContainer>
          <MethodSkeleton />
          <MethodSkeleton />
          <MethodSkeleton />
        </MethodsContainer>
      ) : (
        methods
      )}
      {additionalInfo && (
        <AdditionalInfoSection>
          <Divider />
          {additionalInfo}
        </AdditionalInfoSection>
      )}
    </Container>
  )
}
